/* eslint-disable @typescript-eslint/no-unused-vars */
import { ContentType } from '../ContentType';
import { Messages } from '../../../../gen-src/Localize';
import { IContentDefinition } from './IContentDefinition';
import { CommonColumns, IColumnFactoryParameters } from '../../components/listView/ListViewGridColumnGenerator';
import { IDataQualityWarningInfo, Table } from '../SearchResultTypes';
import { LocalizedCountCell } from '../../components/listView/dataGridCells/PlainTextAndNumberCells';
import { TableNameCell } from '../../components/listView/dataGridCells/TableNameCell';
import { tablesWithCrassCellFormatters } from '@tableau/catalog-ui-temp';
import { GetTablesResult, IDataQualityIndicator, ITable, ITableContainer } from '../httpClient/MetaDataResults';
import GetMetaDataRequests from '../../services/httpClient/GetMetaDataRequests';
import { SearchResponseHitsContent } from '../../../generated/content-exploration';
import { MapperHelper } from '../../../common/utils/MapperHelper';
import { mapCertifier, mapDataQualityWarningOwnerToAuthor, mapDataQualityWarningUpdatedAt } from '../../utils/DataCatalogUtils';
import { SortBy } from '../SortSelection';
import { defaultHeaderFormatter } from '../../components/listView/DefaultHeaderFormatter';
import { SessionManagementService } from '../../services/SessionManagementService';
import { FeatureFlag } from '../../services/FeatureFlagService';
import { LocationTypes } from '../ILocationInfo';
import { ConnectedTagFilter } from '../../components/filters/quickFilter/TagFilter';

export const tableContentDefinition: IContentDefinition<Table, GetTablesResult> = {
  getSectionTitle: () => Messages.TableSectionTitle(),
  getUrl: (urls) => urls.searchTablesUrl(),
  getPath: (urls) => urls.searchTablesPath(),
  defaultDisplayMode: 'list',
  maxItemCountInMixedContentTypeResultPage: 8,
  quickFilters: [ConnectedTagFilter],
  listViewColumns: [
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'tableName',
        dataKey: 'tableName',
        sortable: false,
        headerText: Messages.column_name(),
        growToFit: true,
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: TableNameCell(parameters),
      };
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'tableinworkbookscount',
        dataKey: 'tableinworkbookscount',
        sortable: false,
        direction: 'desc',
        headerText: Messages.column_numWorkbooks(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LocalizedCountCell('workbookCount'),
      };
    },

    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'tableindatasourcecount',
        dataKey: 'tableindatasourcecount',
        sortable: false,
        direction: 'desc',
        headerText: Messages.column_numDatasources(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LocalizedCountCell('datasourceCount'),
      };
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'database',
        dataKey: 'database',
        sortable: false,
        direction: 'desc',
        growToFit: true,
        headerText: Messages.column_table_database(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: tablesWithCrassCellFormatters.database.cellFormatter,
      };
    },
    CommonColumns.description,
    CommonColumns.location,
  ],
  getMetaDataRequests: GetMetaDataRequests.GetTablesRequest,
  mapCRASSData: (crassData: SearchResponseHitsContent[]) => {
    return crassData.map((crassDataItem: SearchResponseHitsContent) => {
      return {
        crassData: crassDataItem,
        __typename: 'DatabaseTable',
        contentType: ContentType.table,
        createdAt: crassDataItem.createdTime ? MapperHelper.toDate(crassDataItem.createdTime) : undefined,
        name: crassDataItem.title || '',
        luid: crassDataItem.luid || '',
        id: crassDataItem.id.toString(),
        graphQLId: crassDataItem.graphQLId,
        vizportalId: crassDataItem.id.toString(),
        description: crassDataItem.description,
      };
    });
  },
  mapMetadata: (searchResultItems, metadata) => {
    const tablesMetadata: { [id: string]: ITable } = {};
    const containerMetadata: { [id: string]: ITableContainer } = {};
    if (metadata.total > 0) {
      metadata.items.tables.forEach((item) => {
        tablesMetadata[item.id] = item;
      });
      metadata.items.containers?.forEach((item) => {
        containerMetadata[item.id] = item;
      });
    }

    return searchResultItems.map((searchResultItem) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const crassDataItem = searchResultItem.crassData!;
      const tablesMetadataItem = tablesMetadata[crassDataItem.id];
      const dataQualityWarnings: IDataQualityIndicator[] = tablesMetadataItem ? tablesMetadataItem.dataQualityWarning : [];
      const dataQualityCertifications: IDataQualityIndicator[] = tablesMetadataItem ? tablesMetadataItem.dataQualityCertifications : [];

      let containerId = '';
      let containerName = '';
      let containerType = '';
      if (FeatureFlag.tablesInProjectsEnabled && FeatureFlag.tablesInProjectsUIEnabled && tablesMetadataItem.containerId !== undefined) {
        if (containerMetadata[tablesMetadataItem.containerId] !== undefined) {
          containerName = containerMetadata[tablesMetadataItem.containerId].name;
          containerId = containerMetadata[tablesMetadataItem.containerId].id;
          containerType = containerMetadata[tablesMetadataItem.containerId].type;
        }
      }

      return {
        ...searchResultItem,
        certificationNote: tablesMetadataItem ? tablesMetadataItem.certificationNote : '',
        certifier: tablesMetadataItem ? mapCertifier(tablesMetadataItem.certifier) : mapCertifier(),
        location: {
          name: containerName,
          id: containerId,
          type: containerType as LocationTypes,
        },
        dataQualityCertifications: dataQualityCertifications.map((dqc) => {
          const dataQualityCertification: IDataQualityWarningInfo = {
            author: mapDataQualityWarningOwnerToAuthor(dqc.owner),
            owner: mapDataQualityWarningOwnerToAuthor(dqc.owner),
            authorDisplayName: dqc.owner?.displayName || '',
            isActive: dqc.isActive,
            isSevere: dqc.isSevere,
            message: dqc.message,
            vizportalId: dqc.id,
            warningType: dqc.type,
            updatedAt: mapDataQualityWarningUpdatedAt(dqc.updatedAt),
            assetUrl: (url: string) => {
              return tablesMetadataItem.luid || '';
            },
            assetName: tablesMetadataItem.name || '',
            assetType: tablesMetadataItem.type || '',
          };

          return dataQualityCertification;
        }),
        dataQualityWarnings: dataQualityWarnings.map((dqw) => {
          const dataQualityWarning: IDataQualityWarningInfo = {
            author: mapDataQualityWarningOwnerToAuthor(dqw.owner),
            owner: mapDataQualityWarningOwnerToAuthor(dqw.owner),
            authorDisplayName: dqw.owner?.displayName || '',
            isActive: dqw.isActive,
            isSevere: dqw.isSevere,
            message: dqw.message,
            vizportalId: dqw.id,
            warningType: dqw.type,
            updatedAt: mapDataQualityWarningUpdatedAt(dqw.updatedAt),
            assetUrl: (url: string) => {
              return tablesMetadataItem.luid;
            },
            assetName: tablesMetadataItem.name,
            assetType: tablesMetadataItem.type,
          };

          return dataQualityWarning;
        }),
        database: {
          __typename: tablesMetadataItem ? tablesMetadataItem.database.dbType : '',
          hostName: tablesMetadataItem ? tablesMetadataItem.hostName : '',
          isGrouped: tablesMetadataItem ? !!tablesMetadataItem.isGroupedParent : false,
          name: tablesMetadataItem ? tablesMetadataItem.name : '',
        },
        site: tablesMetadataItem
          ? { id: tablesMetadataItem.siteId.toString(), luid: tablesMetadataItem.siteLuid, urlName: '' }
          : { id: '', luid: '', urlName: '' },
        isCertified: tablesMetadataItem ? tablesMetadataItem.isCertified : false,
        workbookCount: tablesMetadataItem ? tablesMetadataItem.downstreamWorkbookCount : 0,
        datasourceCount: tablesMetadataItem ? tablesMetadataItem.downstreamDataSourceCount : 0,
        updatedAt: tablesMetadataItem ? MapperHelper.toDate(tablesMetadataItem.updatedAt) : undefined,
      };
    });
  },
  getSortOptions: () => [SortBy.getRelevancySortSelection(), SortBy.getWorkbookPopularity()],
  shouldBeAvailable: () => SessionManagementService.instance.catalogingOnSiteEnabled(),
  favoritable: () => false,
};
